import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Typography,
} from "@material-ui/core"
import * as Sentry from "@sentry/browser"
import Fishbowl from "components/Fishbowl"
import { CurrentAuthContext } from "contexts/CurrentAuth"
import { clientUuid } from "contexts/CurrentPlayer"
import {
  Games,
  useJoinGameMutation,
  useStartGameMutation,
} from "generated/graphql"
import { useTitleStyle } from "index"
import HostRedirect from "pages/Home/Host"
import HowToPlay from "pages/Home/HowToPlay"
import Join from "pages/Home/Join"
import * as React from "react"
import { Trans, useTranslation } from "react-i18next"

enum PlayerState {
  Joining = 1,
  Hosting,
  Choosing,
}

function Home() {
  const { t } = useTranslation()
  const titleClasses = useTitleStyle()
  const currentAuth = React.useContext(CurrentAuthContext)
  const [gameId, setGameId] = React.useState<Games["id"] | null>(null)
  const [playerState, setPlayerState] = React.useState<PlayerState>(
    PlayerState.Choosing
  )
  const [hostGameError, setHostGameError] = React.useState<string | null>(
    null
  )
  const [startGame] = useStartGameMutation()
  const [joinGame] = useJoinGameMutation()

  React.useEffect(() => {
    currentAuth.setJwtToken(null)
  }, [])

  return (
    <>
      <Grid container spacing={5} alignItems="center" direction="column">
        <Grid item>
          <Typography variant="h2" className={titleClasses.title}>
            {t("title", "Fishbowl")}
          </Typography>
        </Grid>
        <Grid item>
          {[PlayerState.Choosing, PlayerState.Hosting].includes(
            playerState
          ) && (
            <>
              {gameId && <HostRedirect gameId={gameId} />}
              <Button
                style={{ marginRight: "10px" }}
                variant="outlined"
                size="large"
                startIcon={
                  playerState === PlayerState.Hosting ? (
                    <CircularProgress size={16} />
                  ) : undefined
                }
                onClick={async () => {
                  setHostGameError(null)
                  setPlayerState(PlayerState.Hosting)
                  try {
                    const { data } = await startGame({
                      context: {
                        headers: {
                          "X-Hasura-Role": "anonymous",
                        },
                      },
                    })
                    const gameId = data?.insert_games_one?.id
                    if (gameId) {
                      const registration = await joinGame({
                        variables: {
                          gameId: gameId,
                          clientUuid: clientUuid(),
                        },
                        context: {
                          headers: {
                            "X-Hasura-Role": "anonymous",
                          },
                        },
                      })
                      if (registration.data?.joinGame) {
                        await currentAuth.setJwtToken(
                          registration.data.joinGame.jwt_token
                        )
                        setGameId(gameId)
                        return
                      }
                    }
                    throw new Error("no game was returned")
                  } catch (e) {
                    Sentry.captureException(e)
                    setHostGameError(
                      t(
                        "home.hostGameError",
                        "Couldn't create a game — the server may just be waking up, try again in a moment."
                      )
                    )
                    setPlayerState(PlayerState.Choosing)
                  }
                }}
                disabled={playerState === PlayerState.Hosting}
              >
                {playerState === PlayerState.Hosting
                  ? t("home.hostGameButtonLoading", "Creating game...")
                  : t("home.hostGameButton", "Host Game")}
              </Button>
              <Button
                size="large"
                variant="outlined"
                disabled={playerState === PlayerState.Hosting}
                onClick={() => setPlayerState(PlayerState.Joining)}
              >
                {t("home.joinGameButton", "Join Game")}
              </Button>
              {hostGameError && (
                <Box pt={2} color="error.main">
                  {hostGameError}
                </Box>
              )}
            </>
          )}
          {playerState === PlayerState.Joining && (
            <Join
              onBack={() => {
                setPlayerState(PlayerState.Choosing)
              }}
            ></Join>
          )}
        </Grid>
        <Grid item>
          <Box pt={2}>
            <Fishbowl></Fishbowl>
          </Box>
        </Grid>
        <Grid item>
          <Box pl={1} pr={1}>
            <Typography variant="h4" className={titleClasses.title}>
              {t("home.whatIsIt.heading", "What is it?")}
            </Typography>
            <Box pt={1}>
              {t(
                "home.whatIsIt.content",
                "Fishbowl is a virtual version of a fun (and mostly hilarious) guessing game, designed for any group of all ages! You'll need at least 4 to play, but it only gets more fun with more players. Hop on a video call, and play through rounds of Taboo, Password, and Charades."
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item>
          <Box pl={1} pr={1}>
            <HowToPlay></HowToPlay>
          </Box>
        </Grid>
      </Grid>
      <Box pt={4} pb={2}>
        <Divider variant="middle"></Divider>
      </Box>
      <Box textAlign="center" pb={4} px={1}>
        <p>
          <Trans t={t} i18nKey="home.footer1">
            {"Free and "}
            <Link href="https://github.com/avimoondra/fishbowl" target="_blank">
              open source
            </Link>
            {", forked with "}
            <span style={{ color: "red" }}>{{ heartIcon: "♥" }}</span>
            {" from "}
            <Link href="https://github.com/avimoondra/fishbowl" target="_blank">
              {{ originalAuthor: "Avi Moondra's" }}
            </Link>
            {" original"}
          </Trans>
        </p>
        <p>
          <Trans t={t} i18nKey="home.footer2">
            {"If you had fun, consider "}
            <Link target="_blank" href="https://buymeacoffee.com/kimish">
              buying me a coffee
            </Link>
            !
          </Trans>
        </p>
      </Box>
    </>
  )
}

export default Home
